import moment from "moment";
import axios from "axios";
import { useLocation, useHistory } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";

import {
  Box,
  Grid,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Chip,
} from "@material-ui/core";
import { GetApp } from "@material-ui/icons";
import InfoIcon from "@material-ui/icons/Info";
import { DataGrid } from "@material-ui/data-grid";
import { blue, green } from "@material-ui/core/colors";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";

import Layout from "../layouts/Layout";
import AddLabel from "../components/AddLabel";
import DateAndTime from "../components/DateAndTime";
import DeviceStatus from "../components/DeviceStatus";
import DeviceSearch from "../components/DeviceSearch";
import ActivateDevice from "../components/ActivateDevice";
import ChangePortGroup from "../components/ChangePortGroup";
import { hasActivateDeactivateAccess } from "../auth/roles";
import DeactivateDevice from "../components/DeactivateDevice";
import DeviceActionHistory from "../components/DeviceActionHistory";

const Devices = (props) => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get("q");
  const [loading, setLoading] = useState(false);
  const [deviceData, setDeviceData] = useState([]);
  const [error, setError] = useState(undefined);
  const [message, setMessage] = useState(undefined);
  const [deviceId, setDeviceId] = useState(searchQuery || "");
  const [activateDeviceData, setActivateDeviceData] = useState(undefined);
  const [rowsCount, setRowsCount] = useState();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [filterModel, setFilterModel] = useState();
  const [sortModel, setSortModel] = React.useState([
    { field: "used_data", sort: "desc" },
  ]);

  const isSearchMode = () => {
    return deviceId !== undefined && deviceId.trim() !== "";
  };

  const getParams = () => {
    let params = { filters: filterModel, sort: sortModel?.[0] };
    if (isSearchMode()) {
      params.q = deviceId;
    }
    return params;
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    if (deviceId && deviceId.trim() !== "") {
      searchParams.set("q", deviceId.trim());
    } else {
      searchParams.delete("q");
    }
    history.replace({ search: searchParams.toString() });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId]);

  const getDevices = (requestParams) => {
    setLoading(true);

    axios
      .post(
        "/devices",
        {
          ...requestParams,
        },
        { params: { page, size: pageSize } }
      )
      .then(({ data }) => {
        setDeviceData(data.items);
        setRowsCount(data.total);
      })
      .catch((error) => {
        setError(error.response.data.detail);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const clearSearch = () => {
    setDeviceId("");
    getDevices({...getParams(), q:''});
  };

  const columns = useRef([
    {
      field: "serial_no",
      headerName: "Serial No.",
      flex: 1,
      minWidth: 250,
      renderCell: (params) => {
        return (
          <Grid
            container
            alignContent="center"
            alignItems="top"
            spacing={1}
            style={{ lineHeight: 1.43 }}
          >
            <Grid item>
              <Grid container alignItems="left" direction="column" spacing={0}>
                <Grid item>
                  <Typography>{params.row.serial_no}</Typography>
                </Grid>
              </Grid>
            </Grid>

            <Grid item>
              <DeviceActionHistory
                device_id={params.row.uuid}
                serial_no={params.row.serial_no}
                setLoading={setLoading}
              />
            </Grid>
          </Grid>
        );
      },
    },
    {
      field: "device_type",
      headerName: "Device Type",
      maxWidth: 100,
      filterable: true,
      sortable: true,
      renderCell: (params) =>
        params.row.device_type && (
          <Grid item>
            <Chip
              color="primary"
              size="small"
              label={params.row.device_type}
            ></Chip>
          </Grid>
        ),
    },
    {
      field: "mac_address",
      headerName: "MAC Address",
      minWidth: 125,
      filterable: true,
    },
    {
      field: "label",
      headerName: "Label",
      flex: 1,
      minWidth: 250,
      filterable: true,
      valueGetter: (params) => params.row.label,
      renderCell: (params) => {
        return (
          <AddLabel
            serial_no={params.row.serial_no}
            device_id={params.row.uuid}
            label={params.row.label}
            setLoading={setLoading}
          />
        );
      },
    },
    {
      field: "port_group",
      headerName: "Port Groups",
      flex: 1,
      minWidth: 260,
      filterable: true,
      renderCell: (params) => {
        return (
          <ChangePortGroup
            port_group={params.row.port_group}
            device_uuid={params.row.uuid}
            device_name={params.row.name}
            setLoading={setLoading}
          />
        );
      },
    },
    {
      field: "status_formatted",
      headerName: "Status",
      minWidth: 150,
      filterable: true,
      renderCell: (params) => (
        <DeviceStatus
          status={params.row.status}
          status_formatted={params.row.status_formatted}
        />
      ),
    },
    {
      field: "used_data",
      headerName: "Used Data (MB)",
      filterable: true,
      minWidth: 160,
      renderCell: (params) => (
        <Typography>{params.row.used_data_formatted}</Typography>
      ),
    },
    {
      field: "reseller_id",
      headerName: "Reseller",
      filterable: true,
      minWidth: 100,
      renderCell: (params) => (
        <Typography>{params.row.parent_reseller_name}</Typography>
      ),
    },
    {
      field: "last_updated_at",
      headerName: "Last Updated At",
      filterable: false,
      minWidth: 160,
      valueGetter: (params) =>
        params.row.last_updated_at &&
        parseInt(moment(params.row.last_updated_at).format("x")),
      renderCell: (params) =>
        params.row.last_updated_at && (
          <DateAndTime dateAndTime={params.row.last_updated_at} />
        ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      minWidth: 200,
      filterable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <React.Fragment>
            <Grid container justifyContent="flex-start" alignItems="center">
              {hasActivateDeactivateAccess() && !params.row.is_activated && (
                <Grid item>
                  <Tooltip title="Activate Device">
                    <IconButton
                      onClick={() => {
                        setActivateDeviceData(params.row);
                      }}
                      size="small"
                    >
                      <PlayArrowIcon
                        style={{
                          color: green[500],
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Grid>
              )}
              {hasActivateDeactivateAccess() && params.row.is_activated && (
                <DeactivateDevice
                  deviceData={params.row}
                  setMessage={setMessage}
                  setError={setError}
                />
              )}

              <Grid item>
                <Tooltip title="Device Details">
                  <a
                    href={`/device/${params.row.uuid}/info`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <IconButton size="small">
                      <InfoIcon style={{ color: blue[500] }} />
                    </IconButton>
                  </a>
                </Tooltip>
              </Grid>
            </Grid>
          </React.Fragment>
        );
      },
    },
  ]);

  const downloadExportData = () => {
    setLoading(true);
    axios.get(`/devices/export`).then(({ data }) => {
      const url = window.URL.createObjectURL(
        new Blob([data], { type: "text/csv" })
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `sparqfi_device_export_${moment().format("YYYYMMDDhmmss")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      setLoading(false);
    });
  };

  useEffect(() => {
    getDevices(getParams());
    // eslint-disable-next-line
  }, [page, filterModel, pageSize, sortModel]);

  return (
    <Layout
      loading={loading}
      error={error}
      successMessage={message}
      title={props.title}
      containerMaxWidth="xl"
    >
      <Box pt={3}>
        <Grid container direction="row" justifyContent="space-between">
          <Grid item xs={12} md={8} lg={8}>
            <DeviceSearch
              value={deviceId}
              setValue={setDeviceId}
              handleSubmit={()=> getDevices(getParams())}
              handleClear={clearSearch}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadExportData}
              startIcon={<GetApp />}
            >
              Export
            </Button>
          </Grid>
        </Grid>

        <React.Fragment>
          <Box pt={2}>
            <DataGrid
              rows={deviceData}
              columns={columns.current}
              disableSelectionOnClick
              autoHeight
              rowHeight={100}
              sortModel={sortModel}
              onPageChange={(newPage) => setPage(newPage + 1)}
              pageSize={pageSize}
              paginationMode="server"
              rowCount={rowsCount}
              initialState={{
                pagination: {
                  page: 1,
                },
              }}
              onPageSizeChange={setPageSize}
              filterMode="server"
              loading={loading}
              getRowId={(row) => row.uuid}
              onSortModelChange={(model) => setSortModel(model)}
              onFilterModelChange={(model) => {
                setFilterModel(model);
              }}
              density="compact"
            />
          </Box>
        </React.Fragment>

        {/* <ChangePortGroup
          open={changePortGroupDeviceData !== undefined}
          onClose={() => {
            setChangePortGroupDeviceData(undefined);
          }}
          deviceData={changePortGroupDeviceData}
        ></ChangePortGroup> */}

        <ActivateDevice
          open={activateDeviceData !== undefined}
          onClose={() => {
            setActivateDeviceData(undefined);
          }}
          deviceData={activateDeviceData}
        ></ActivateDevice>
      </Box>
    </Layout>
  );
};

Devices.defaultProps = {
  title: "Manage Devices",
};

export default Devices;
